import React from "react";
import { useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarGroup,
  SidebarGroupContent,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { ChevronDown, ChevronRight } from "lucide-react"

import { MSG_TABS } from './constants/msgTabs';
import { isTabActive } from './utils/tabUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Recursive component to render nested menu items
const NestedMenu = ({ item, onTabChange, currentPath, level = 0 }) => {
  const hasChildren = item.items && item.items.length > 0;
  const isActive = isTabActive(item.key, currentPath);
  const isParentActive = hasChildren && item.items.some(child => isTabActive(child.key, currentPath));
  const [isOpen, setIsOpen] = React.useState(true); // Always start expanded
  
  // Still auto-expand parent items when active (in case they were manually collapsed)
  React.useEffect(() => {
    if (isParentActive) {
      setIsOpen(true);
    }
  }, [isParentActive]);

  if (!hasChildren) {
    return (
      <SidebarMenuItem key={item.key}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
        >
          <button
            onClick={() => onTabChange?.(item.key)}
            className={`settings-sidebar-item flex items-center gap-3 w-full text-left py-2 px-3 rounded-md transition-colors ${level > 0 ? 'pl-8' : ''}`}
          >
            {item.icon && (
              <FontAwesomeIcon
                icon={item.icon}
                className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
              />
            )}
            <span className={isActive ? "text-primary font-medium" : "text-muted-foreground"}>
              {item.label}
            </span>
          </button>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem key={item.key}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="group/collapsible w-full">
        <CollapsibleTrigger 
          className={`flex w-full items-center px-3 py-2 text-left text-muted-foreground hover:text-primary font-medium transition-colors ${level > 0 ? 'pl-8' : ''}`}
        >
          {item.icon && (
            <FontAwesomeIcon
              icon={item.icon}
              className={`w-4 h-4 ${isActive || isParentActive ? 'text-primary' : 'text-muted-foreground'}`}
            />
          )}
          <span className={isActive || isParentActive ? "text-primary font-medium" : "text-muted-foreground"}>
            {item.label}
          </span>
          <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items.map((child) => (
              <NestedMenu 
                key={child.key} 
                item={child} 
                onTabChange={onTabChange} 
                currentPath={currentPath}
                level={level + 1}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
};





function MSGSidebar({
  active,
  onTabChange,
}: {
  active?: string;
  onTabChange?: (key: string) => void;
}) {
  // Use the active prop as currentPath for highlighting
  return (
    <div className="h-full min-h-screen flex flex-col bg-card border-r border-border">
      <SidebarContent className="flex-1 h-full overflow-y-auto 
        scrollbar-thin 
        scrollbar-thumb-primary/20 
        hover:scrollbar-thumb-primary/30 
        dark:scrollbar-thumb-primary/30 
        dark:hover:scrollbar-thumb-primary/40 
        scrollbar-track-transparent 
        scrollbar-thumb-rounded-full 
        transition-colors duration-200
        pr-1 -mr-1 py-1">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {MSG_TABS.map((item) => (
                <NestedMenu 
                  key={item.key} 
                  item={item} 
                  onTabChange={onTabChange} 
                  currentPath={active}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </div>
  );
}


// Only export the React component
export { MSGSidebar };